import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, AlertTriangle } from 'lucide-react';
import { useProgress } from '../context/ProgressContext';
export function OnlineSafetyPage() {
  const navigate = useNavigate();
  const {
    progress
  } = useProgress();
  const modules = [{
    id: 'privacy-basics',
    title: 'Protecting Your Privacy',
    icon: Lock,
    description: 'Keep your personal information safe online',
    color: 'from-blue-500 to-cyan-500',
    content: ['Never share your full name, address, school name, or phone number publicly', 'Use strong, unique passwords for each account (not your birthday!)', 'Enable two-factor authentication on all accounts', 'Check your privacy settings - make accounts private if possible', "Don't share your location in real-time (turn off location tags)", 'Be careful what you post - once online, it can be screenshot and shared', "Don't accept friend requests from people you don't know in real life"]
  }, {
    id: 'online-strangers',
    title: 'Dealing with Online Strangers',
    icon: Eye,
    description: 'Stay safe when interacting with people online',
    color: 'from-orange-500 to-red-500',
    content: ['People can lie about their age, gender, and identity online', "Photos can be stolen - don't trust profile pictures", "If someone asks you to keep your friendship secret, that's a red flag", 'Never send intimate or revealing photos to ANYONE (even if you trust them)', 'If someone makes you uncomfortable, block them immediately', "Don't meet online contacts in person without adult supervision", "If you do meet: public place, bring friends, tell adults where you're going"]
  }, {
    id: 'cyberbullying',
    title: 'Handling Cyberbullying',
    icon: Shield,
    description: 'Protect yourself from online harassment',
    color: 'from-purple-500 to-pink-500',
    content: ["Don't respond to bullies - it gives them what they want (attention)", 'Screenshot everything as evidence before blocking', 'Block the person on all platforms', 'Report to the platform (Instagram, TikTok, etc.)', 'Tell a trusted adult - parent, teacher, or counselor', "It's not your fault - bullies are the problem, not you", 'You can report cyberbullying to cybercrime.gov.in']
  }, {
    id: 'digital-footprint',
    title: 'Your Digital Footprint',
    icon: AlertTriangle,
    description: 'Think before you post',
    color: 'from-green-500 to-teal-500',
    content: ['Everything you post online creates a permanent digital footprint', 'Future employers and colleges can see your social media', "Even 'deleted' content can be recovered or screenshot", 'Before posting, ask: Would I be okay if my parents/teachers saw this?', 'Think twice before posting when angry or emotional', "Don't post anything you wouldn't want on a billboard with your name", 'Your online reputation matters - protect it!']
  }];
  return <div className="min-h-screen w-full bg-gradient-to-br from-orange-50 via-red-50 to-pink-50 dark:from-slate-900 dark:via-orange-950 dark:to-red-950 pb-20 md:pb-8" data-id="element-744">
      <header className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border-b border-orange-100 dark:border-slate-700 sticky top-0 z-10" data-id="element-745">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between" data-id="element-746">
          <button onClick={() => navigate('/home')} className="flex items-center space-x-2 text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300" data-id="element-747">
            <ArrowLeft className="w-5 h-5" data-id="element-748" />
            <span className="font-medium" data-id="element-749">Home</span>
          </button>
          <h1 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-750">
            Online Safety
          </h1>
          <div className="w-20" data-id="element-751" />
        </div>
      </header>
      <div className="max-w-6xl mx-auto px-4 py-8" data-id="element-752">
        <div className="text-center mb-8" data-id="element-753">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3" data-id="element-754">
            Stay Safe Online, {progress.preferences.name || 'Friend'}! 🛡️
          </h2>
          <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto" data-id="element-755">
            Learn how to protect yourself online, spot red flags, and navigate
            the digital world safely!
          </p>
        </div>
        <div className="space-y-6" data-id="element-756">
          {modules.map(module => {
          const Icon = module.icon;
          return <div key={module.id} className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg overflow-hidden" data-id="element-757">
                <div className={`h-24 bg-gradient-to-br ${module.color} flex items-center px-6`} data-id="element-758">
                  <Icon className="w-10 h-10 text-white mr-4" data-id="element-759" />
                  <div data-id="element-760">
                    <h3 className="text-2xl font-bold text-white" data-id="element-761">
                      {module.title}
                    </h3>
                    <p className="text-white/90 text-sm" data-id="element-762">
                      {module.description}
                    </p>
                  </div>
                </div>
                <div className="p-6" data-id="element-763">
                  <ul className="space-y-3" data-id="element-764">
                    {module.content.map((item, index) => <li key={index} className="flex items-start space-x-3" data-id="element-765">
                        <div className={`w-6 h-6 bg-gradient-to-br ${module.color} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`} data-id="element-766">
                          <span className="text-white text-xs font-bold" data-id="element-767">
                            {index + 1}
                          </span>
                        </div>
                        <p className="text-gray-700 dark:text-gray-300" data-id="element-768">
                          {item}
                        </p>
                      </li>)}
                  </ul>
                </div>
              </div>;
        })}
        </div>
        <div className="mt-8 bg-red-50 dark:bg-slate-800 border-l-4 border-red-500 rounded-lg p-6" data-id="element-769">
          <h4 className="font-bold text-red-900 dark:text-red-300 mb-2" data-id="element-770">
            Trust Your Instincts! 🚨
          </h4>
          <p className="text-red-800 dark:text-red-200 mb-3" data-id="element-771">
            If something or someone online makes you uncomfortable, trust that
            feeling! Block, report, and tell a trusted adult. You're never
            overreacting when it comes to your safety.
          </p>
          <p className="text-red-800 dark:text-red-200 font-semibold" data-id="element-772">
            Emergency Helplines: Childline India - 1098 | Cyber Crime - 1930 or
            cybercrime.gov.in
          </p>
        </div>
      </div>
    </div>;
}